import { useMemo, useState } from "react";
import { ApplicationLocalNavigation, ApplicationStateRegion } from "../../../components/application";
import { DataTable } from "../../../components/data-display";
import { FacilitatorResultCommentary } from "./ByceG3FacilitatorEvidence";

const PARAMETERS = ["Overall Severity", "Roughness", "Breathiness", "Strain"];
const FAMILY_ITEMS = [
  { value: "session-1-1", label: "Session 1.1", id: "cohort-session-tab", controls: "cohort-session-panel" },
  { value: "reflections", label: "Reflections", id: "cohort-reflections-tab", controls: "cohort-reflections-panel" },
  { value: "stage-4", label: "Stage 4", id: "cohort-stage4-tab", controls: "cohort-stage4-panel" },
];
const componentByKey = (evidence, key) => evidence?.components?.find(component => component.key === key);
const displayNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(number) : value;
};

export const parseCohortAggregate = (item, index = 0) => {
  const [sampleName, parameter] = item.label.split(" · ");
  const match = item.value.match(/^Learner cohort N (\d+)(?: · Mean ([^·]+))? · PVQD reference N (\d+) · Observed range ([^·]+)$/);
  if (!match) return null;
  return { id: item.label, voiceNumber: index + 1, sampleName, parameter, cohortN: Number(match[1]), cohortMean: match[2] ? displayNumber(match[2].trim()) : "—", pvqdN: Number(match[3]), pvqdRange: match[4].trim() };
};

const aggregateRows = component => (component?.items || []).map(parseCohortAggregate).filter(Boolean);
const SESSION_COLUMNS = [
  { key: "voice", label: "Voice", getValue: row => `Voice ${row.voiceNumber} · ${row.sampleName}` },
  { key: "cohortMean", label: "Cohort mean" },
  { key: "cohortN", label: "Cohort N" },
  { key: "pvqdRange", label: "PVQD observed range" },
  { key: "pvqdN", label: "PVQD N" },
];
const STAGE4_COLUMNS = [
  { key: "parameter", label: "Parameter" },
  { key: "cohortMean", label: "Cohort mean" },
  { key: "cohortN", label: "Cohort N" },
  { key: "pvqdRange", label: "PVQD observed range" },
  { key: "pvqdN", label: "PVQD N" },
];

export const parseCohortReflection = (item, participants) => {
  const separator = item.label.lastIndexOf(" · ");
  const participantRef = separator >= 0 ? item.label.slice(separator + 3) : "";
  const match = item.value.match(/^Source session ([^·]+) · Selected (Voice [12]|Voice unavailable) · Submitted ([^·]+) · ([\s\S]*)$/);
  if (!match) return null;
  const participant = participants.find(row => String(row.participantRef) === participantRef);
  return {
    id: item.label,
    participant: participant?.displayIdentity || "Project member",
    email: participant?.email || "—",
    selectedVoice: match[2],
    submittedAt: match[3].trim(),
    text: match[4],
  };
};

function ResultNote({ component, contextKey, program, projectId }) {
  return <FacilitatorResultCommentary fixedProgramStepKey={contextKey} projectId={projectId} program={program} resultLabel={component.label} />;
}

function SessionResults({ component, program, projectId }) {
  const rows = aggregateRows(component);
  return <section aria-labelledby="cohort-session-heading" className="av-byce-cohort-result-section">
    <h3 id="cohort-session-heading">Session 1.1 · Overall Severity</h3>
    <p className="av-project-facilitator-overview__metadata">Learner cohort and PVQD reference populations remain separate.</p>
    {rows.length ? <DataTable caption="Session 1.1 cohort aggregate" columns={SESSION_COLUMNS} density="compact" minWidth="42rem" rowKey="id" rows={rows} /> : <ApplicationStateRegion status="empty" message="Session 1.1 cohort evidence is unavailable." />}
    <ResultNote component={component} contextKey="g3-stage-1-session-1" program={program} projectId={projectId} />
  </section>;
}

function ReflectionResults({ evidence, participants, program, projectId }) {
  const components = [1, 2, 3, 4].map(index => componentByKey(evidence, `reflection-cohort-g3-stage-3-session-${index}-reflection`));
  return <div className="av-byce-cohort-reflections">{components.map((component, index) => {
    const rows = component?.type === "metadata" ? component.items.map(item => parseCohortReflection(item, participants)).filter(Boolean) : [];
    const contextKey = `g3-stage-3-session-${index + 1}-reflection`;
    return <section className="av-byce-cohort-result-section" key={contextKey}>
      <h3>Listening session 3.{index + 1}</h3>
      {rows.length ? <div className="av-byce-cohort-reflection-list">{rows.map(row => <article className="av-byce-cohort-reflection" key={row.id}>
        <header><strong>{row.participant}</strong><span>{row.email}</span></header>
        <dl><div><dt>Selected</dt><dd>{row.selectedVoice}</dd></div><div><dt>Submitted</dt><dd>{new Date(row.submittedAt).toLocaleString()}</dd></div></dl>
        <p>{row.text}</p>
      </article>)}</div> : <p>No submitted Reflections are available.</p>}
      <ResultNote component={component || { label: `Reflection 3.${index + 1}` }} contextKey={contextKey} program={program} projectId={projectId} />
    </section>;
  })}</div>;
}

function Stage4Results({ component, program, projectId }) {
  const voices = useMemo(() => {
    const groups = new Map();
    for (const row of aggregateRows(component)) {
      if (!groups.has(row.sampleName)) groups.set(row.sampleName, { sampleName: row.sampleName, voiceNumber: groups.size + 1, rows: [] });
      groups.get(row.sampleName).rows.push(row);
    }
    return [...groups.values()].map(voice => ({ ...voice, rows: PARAMETERS.map(parameter => voice.rows.find(row => row.parameter === parameter)).filter(Boolean) }));
  }, [component]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = voices[selectedIndex];
  return <section aria-labelledby="cohort-stage4-heading" className="av-byce-cohort-result-section">
    <h3 id="cohort-stage4-heading">Stage 4 cohort results</h3>
    <p className="av-project-facilitator-overview__metadata">Released descriptive evidence; no score or scoring window.</p>
    {selected ? <>
      <div aria-label="Stage 4 Voices" className="av-byce-cohort-voice-navigation">{voices.map((voice, index) => <button aria-current={index === selectedIndex ? "true" : undefined} aria-label={`Voice ${voice.voiceNumber}`} key={voice.sampleName} onClick={() => setSelectedIndex(index)} type="button">{voice.voiceNumber}</button>)}</div>
      <h4>Voice {selected.voiceNumber} <span>· {selected.sampleName}</span></h4>
      <DataTable caption={`Stage 4 Voice ${selected.voiceNumber} cohort aggregate`} columns={STAGE4_COLUMNS} density="compact" minWidth="38rem" rowKey="id" rows={selected.rows} />
    </> : <ApplicationStateRegion status="empty" message="Stage 4 cohort evidence is not released." />}
    <ResultNote component={component || { label: "Stage 4 cohort results" }} contextKey="g3-stage-4-final-rating" program={program} projectId={projectId} />
  </section>;
}

export default function ByceG3CohortResults({ evidence, participants = [], program, projectId }) {
  const [family, setFamily] = useState("session-1-1");
  const activeItem = FAMILY_ITEMS.find(item => item.value === family);
  return <>
    <ApplicationLocalNavigation ariaLabel="Cohort result family" items={FAMILY_ITEMS} value={family} onChange={setFamily} />
    <div aria-labelledby={activeItem.id} id={activeItem.controls} role="tabpanel">
      {family === "session-1-1" ? <SessionResults component={componentByKey(evidence, "session-1-1-cohort")} program={program} projectId={projectId} /> : null}
      {family === "reflections" ? <ReflectionResults evidence={evidence} participants={participants} program={program} projectId={projectId} /> : null}
      {family === "stage-4" ? <Stage4Results component={componentByKey(evidence, "stage-4-cohort")} program={program} projectId={projectId} /> : null}
    </div>
  </>;
}
